import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import {
  CheckCircle, XCircle, Clock, FileText, User, Calendar, AlertTriangle, Shield,
  Search, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Inbox, DollarSign
} from 'lucide-react';
import { formatCurrencyAmount } from '../components/CurrencyDisplay';
import TablePagination from '../components/TablePagination';

const EAQ_PAGE_SIZE = 25;

export default function ExpenseApprovalQueue() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [isFinanceAdmin, setIsFinanceAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [comments, setComments] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('DaysPending');
  const [sortDirection, setSortDirection] = useState('desc');
  const [eaqPage, setEaqPage] = useState(1);
  const [activeTab, setActiveTab] = useState('pending');
  const [expenseHistory, setExpenseHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchApprovalQueue();
  }, []);

  const fetchApprovalQueue = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/expenses/approval-queue');
      setExpenses(response.data.expenses || []);
      setIsFinanceAdmin(response.data.isFinanceAdmin || false);
    } catch (error) {
      console.error('Failed to fetch approval queue:', error);
      alert('Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await apiClient.get('/expenses/approval-history');
      setExpenseHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch expense approval history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history' && expenseHistory.length === 0) {
      fetchApprovalHistory();
    }
  }, [activeTab]);

  const handleApprove = (expense) => {
    setSelectedExpense(expense);
    setModalType('approve');
    setComments('');
    setShowModal(true);
  };

  const handleReject = (expense) => {
    setSelectedExpense(expense);
    setModalType('reject');
    setComments('');
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (modalType === 'reject' && !comments.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessing(selectedExpense.ExpenseID);

    try {
      if (modalType === 'approve') {
        await apiClient.post(`/expenses/${selectedExpense.ExpenseID}/approve`, {
          comments: comments.trim() || null
        });
        alert('Expense approved successfully');
      } else {
        await apiClient.post(`/expenses/${selectedExpense.ExpenseID}/reject`, {
          comments: comments.trim()
        });
        alert('Expense rejected');
      }

      // Refresh queue
      await fetchApprovalQueue();
      setShowModal(false);
      setSelectedExpense(null);
      setComments('');
    } catch (error) {
      console.error('Approval action error:', error);
      alert(error.response?.data?.message || `Failed to ${modalType} expense`);
    } finally {
      setProcessing(null);
    }
  };

  const getDaysPendingBadge = (days) => {
    if (days <= 2) return <span className="text-green-600 text-xs font-medium">{days}d</span>;
    if (days <= 5) return <span className="text-yellow-600 text-xs font-semibold">{days}d</span>;
    return (
      <span className="inline-flex items-center text-red-600 text-xs font-bold">
        <AlertTriangle className="w-3 h-3 mr-0.5" />
        {days}d
      </span>
    );
  };

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Filter + sort
  const filteredExpenses = useMemo(() => {
    let list = expenses;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(e =>
        (e.ExpenseNumber || '').toLowerCase().includes(q) ||
        (e.EmployeeName || '').toLowerCase().includes(q) ||
        (e.Purpose || '').toLowerCase().includes(q) ||
        (e.DepartmentName || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (['TotalAmount', 'DaysPending'].includes(sortField)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'ExpenseDate') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expenses, searchTerm, sortField, sortDirection]);
  const pagedExpensesQueue = useMemo(
    () => filteredExpenses.slice((eaqPage - 1) * EAQ_PAGE_SIZE, eaqPage * EAQ_PAGE_SIZE),
    [filteredExpenses, eaqPage]
  );
  // Reset page on filter/sort change
  React.useEffect(() => { setEaqPage(1); }, [searchTerm, sortField, sortDirection]);

  const totalValue = expenses.reduce((sum, e) => sum + (parseFloat(e.TotalAmount) || 0), 0);
  const overdueCount = expenses.filter(e => e.DaysPending > 5).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-7 h-7 text-blue-600" />
            Expense Approval Queue
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isFinanceAdmin
              ? 'All pending expense claims across the company'
              : 'Review and approve expense claims from your team'}
          </p>
        </div>
        <button
          onClick={fetchApprovalQueue}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Approval</p>
              <p className="text-2xl font-bold text-gray-900">{expenses.length}</p>
            </div>
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrencyAmount(totalValue, 'GBP')}</p>
            </div>
            <DollarSign className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Overdue (&gt;5 days)</p>
              <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{overdueCount}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pending'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending
              {expenses.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {expenses.length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              History
            </span>
          </button>
        </nav>
      </div>

      {activeTab === 'pending' && <>
      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search expense, employee, purpose..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <span className="text-sm text-gray-500 self-center">
            {filteredExpenses.length} of {expenses.length} expenses
          </span>
        )}
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ExpenseNumber')}>
                  <span className="inline-flex items-center">Expense # <SortIcon field="ExpenseNumber" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('EmployeeName')}>
                  <span className="inline-flex items-center">Employee <SortIcon field="EmployeeName" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ExpenseDate')}>
                  <span className="inline-flex items-center">Date <SortIcon field="ExpenseDate" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purpose
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('TotalAmount')}>
                  <span className="inline-flex items-center justify-end">Amount <SortIcon field="TotalAmount" /></span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('DaysPending')}>
                  <span className="inline-flex items-center">Waiting <SortIcon field="DaysPending" /></span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipts
                </th>
                {isFinanceAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={isFinanceAdmin ? 9 : 8} className="px-4 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={isFinanceAdmin ? 9 : 8} className="px-4 py-12 text-center">
                    <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">
                      {searchTerm ? 'No expenses match your search' : 'No expenses pending approval'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {searchTerm ? 'Try adjusting your search' : "You're all caught up!"}
                    </p>
                  </td>
                </tr>
              ) : (
                pagedExpensesQueue.map((expense) => {
                  const isOverdue = expense.DaysPending > 5;
                  return (
                  <tr key={expense.ExpenseID} className={`transition-colors group ${isOverdue ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/expenses/${expense.ExpenseID}`}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        {expense.ExpenseNumber}
                      </Link>
                      {expense.IsFinalApprover && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Final
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {expense.EmployeeName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {expense.DepartmentName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {new Date(expense.ExpenseDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-[200px] truncate" title={expense.Purpose}>
                        {expense.Purpose}
                      </div>
                      {expense.Category && (
                        <div className="text-xs text-gray-500">{expense.Category}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                      {formatCurrencyAmount(expense.TotalAmount, expense.Currency)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {getDaysPendingBadge(expense.DaysPending)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm">
                      {expense.DocumentCount > 0 ? (
                        <span className="inline-flex items-center text-green-600">
                          <FileText className="w-4 h-4 mr-1" />
                          {expense.DocumentCount}
                        </span>
                      ) : (
                        <span className="text-yellow-600 inline-flex items-center">
                          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                          None
                        </span>
                      )}
                    </td>
                    {isFinanceAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {expense.IsAssignedToMe ? (
                          <span className="inline-flex items-center text-blue-700 font-medium">
                            <Shield className="w-4 h-4 mr-1" />
                            You
                          </span>
                        ) : (
                          <span className="text-gray-600">
                            {expense.CurrentApproverName || 'Unassigned'}
                          </span>
                        )}
                      </td>
                    )}
                    <td className={`px-4 py-3 whitespace-nowrap text-right sticky right-0 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)] transition-colors ${isOverdue ? 'bg-red-50/40 group-hover:bg-red-50' : 'bg-white group-hover:bg-gray-50'}`}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(expense)}
                          disabled={processing === expense.ExpenseID}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          {isFinanceAdmin && !expense.IsAssignedToMe ? 'Take & Approve' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(expense)}
                          disabled={processing === expense.ExpenseID}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TablePagination page={eaqPage} pageSize={EAQ_PAGE_SIZE} total={filteredExpenses.length} onPageChange={setEaqPage} />
      </div>
      </>}

      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {historyLoading ? (
            <div className="px-4 py-12 text-center">
              <div className="inline-flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-500">Loading history...</span>
              </div>
            </div>
          ) : expenseHistory.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <CheckCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 font-medium">No approval history yet</p>
              <p className="text-gray-400 text-sm mt-1">Expenses you approve or reject will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expense #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Decision</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenseHistory.map((item) => (
                    <tr key={`${item.ExpenseID}-${item.ActionDate}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link to={`/expenses/${item.ExpenseID}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                          {item.ExpenseNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.EmployeeName}</div>
                        <div className="text-xs text-gray-500">{item.DepartmentName}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.ExpenseDate ? new Date(item.ExpenseDate).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="max-w-[180px] truncate" title={item.Purpose}>{item.Purpose}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                        {formatCurrencyAmount(item.TotalAmount, item.Currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {item.Decision === 'Approved' ? (
                          <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full text-xs font-medium">
                            <XCircle className="w-3 h-3" /> Rejected
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.ActionDate ? new Date(item.ActionDate).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="max-w-[200px] truncate" title={item.Comments}>{item.Comments || '—'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Approval Modal */}
      {showModal && selectedExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {modalType === 'approve' ? 'Approve Expense' : 'Reject Expense'}
            </h3>

            <div className="mb-4 p-4 bg-gray-50 rounded">
              <div className="text-sm text-gray-600">Expense:</div>
              <div className="font-medium text-gray-900">{selectedExpense.ExpenseNumber}</div>
              <div className="text-sm text-gray-600 mt-2">Employee:</div>
              <div className="font-medium text-gray-900">{selectedExpense.EmployeeName}</div>
              <div className="text-sm text-gray-600 mt-2">Amount:</div>
              <div className="font-bold text-gray-900">
                {formatCurrencyAmount(selectedExpense.TotalAmount, selectedExpense.Currency)}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments {modalType === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={
                  modalType === 'approve' 
                    ? 'Optional comments...' 
                    : 'Please provide reason for rejection...'
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                required={modalType === 'reject'}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={confirmAction}
                disabled={processing}
                className={`flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors disabled:opacity-50 ${
                  modalType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {processing ? 'Processing...' : `Confirm ${modalType === 'approve' ? 'Approval' : 'Rejection'}`}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedExpense(null);
                  setComments('');
                }}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
